import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    SkeletonShapeAnimationConstantType,
    SkeletonShapeBandRangeConstantType,
    SkeletonShapeColorsConstantType,
    SkeletonShapeDynamicStyleType,
    SkeletonShapeColors
} from "./type";
import type {
    Mutable
} from "../../../../types";
import type {
    SkeletonAnimation
} from "../../type";
import {
    getDirectionSign
} from "../../../../helpers/direction";
import {
    withAlpha,
    webStyle
} from "../../../../utils";

export const SKELETON_BAND_WIDTH_RATIO = 0.5;

export const getSkeletonShapeColors = ({
    colors
}: SkeletonShapeColorsConstantType): SkeletonShapeColors => {
    const peakColor = colors.system.state.overlay.pressed.default;

    return {
        baseColor: colors.system.state.overlay.hover.default,
        clearColor: withAlpha({
            color: peakColor,
            alpha: 0
        }),
        peakColor
    };
};

export const getSkeletonAnimation = ({
    isReduceMotionEnabled,
    loopDuration,
    animation
}: SkeletonShapeAnimationConstantType): SkeletonAnimation => {
    if(!loopDuration) {
        return "none";
    }

    if(isReduceMotionEnabled && animation === "shimmer") {
        return "pulse";
    }

    return animation;
};

export const getSkeletonBandRange = ({
    direction,
    width
}: SkeletonShapeBandRangeConstantType): Array<number> => {
    const directionSign = getDirectionSign({
        direction
    });

    return [
        -width * SKELETON_BAND_WIDTH_RATIO * directionSign,
        width * directionSign
    ];
};

const stylesheet = StyleSheet.create({
    shimmerEnd: webStyle({
        animationKeyframes: {
            to: {
                transform: `translateX(-${100 / SKELETON_BAND_WIDTH_RATIO}%)`
            },
            from: {
                transform: "translateX(100%)"
            }
        },
        animationIterationCount: "infinite",
        animationTimingFunction: "linear"
    }),
    shimmerStart: webStyle({
        animationKeyframes: {
            to: {
                transform: `translateX(${100 / SKELETON_BAND_WIDTH_RATIO}%)`
            },
            from: {
                transform: "translateX(-100%)"
            }
        },
        animationIterationCount: "infinite",
        animationTimingFunction: "linear"
    }),
    pulseBase: webStyle({
        animationTimingFunction: "ease-in-out",
        animationIterationCount: "infinite",
        animationKeyframes: {
            "0%, 100%": {
                opacity: 1
            },
            "50%": {
                opacity: 0
            }
        }
    }),
    pulsePeak: webStyle({
        animationTimingFunction: "ease-in-out",
        animationIterationCount: "infinite",
        animationKeyframes: {
            "0%, 100%": {
                opacity: 0
            },
            "50%": {
                opacity: 1
            }
        }
    }),
    layer: {
        ...StyleSheet.absoluteFill
    },
    shape: {
        pointerEvents: "none",
        overflow: "hidden",
        flexShrink: 0
    },
    band: {
        position: "absolute",
        bottom: 0,
        start: 0,
        top: 0
    }
});

export const useStyles = ({
    currentColors,
    loopDuration,
    phaseDelay,
    animation,
    bandWidth
}: SkeletonShapeDynamicStyleType) => {
    const styles = {
        webBand: webStyle({
            backgroundImage: `linear-gradient(90deg, ${currentColors.clearColor}, ${currentColors.peakColor}, ${currentColors.clearColor})`,
            width: `${SKELETON_BAND_WIDTH_RATIO * 100}%`
        }) as Mutable<ViewStyle>,
        band: {
            width: bandWidth * SKELETON_BAND_WIDTH_RATIO
        } as Mutable<ViewStyle>,
        baseLayer: {
            backgroundColor: currentColors.baseColor
        } as Mutable<ViewStyle>,
        peakLayer: {
            backgroundColor: currentColors.peakColor
        } as Mutable<ViewStyle>,
        webAnimation: webStyle({
            animationDuration: `${loopDuration}ms`,
            animationDelay: `${phaseDelay}ms`
        }) as Mutable<ViewStyle>,
        shape: {} as Mutable<ViewStyle>
    };

    if(animation !== "pulse") {
        styles.shape.backgroundColor = currentColors.baseColor;
    }

    return styles;
};
export default stylesheet;
